// TreeList (the price book round, DEC-019): where things sit, as a short tree beside the list it
// filters: systems › tasks, with how many each holds, at most three levels deep. "All items" on top;
// the one that's open marked with the ink rule on its left; a system opens when it's chosen, its
// tasks indented under it. A link at the foot adds one (New category). The keyboard walks it: ↑ ↓
// between rows, → opens a system, ← closes it or goes to its system, Home and End. Not for a menu of
// places (the rail) or a choice in a form (ChoiceGroup). Reads only --tree-*.
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import './TreeList.css';

export interface TreeNode {
  id: string;
  label: string;
  count?: number;
  children?: TreeNode[];
}

export interface TreeListProps {
  /** What it is, for screen readers ("Where items sit"). */
  label: string;
  /** The row above the tree that shows everything ("All items"), chosen as null. */
  root?: { label: string; count?: number };
  nodes: TreeNode[];
  /** The open one: a node's id, or null for the root. */
  value: string | null;
  onChange: (id: string | null) => void;
  /** Which systems are open; by default the one holding `value`. */
  defaultExpanded?: string[];
  /** Under the tree: New category. */
  foot?: ReactNode;
}

const MAX_DEPTH = 3;

function pathTo(nodes: TreeNode[], id: string | null, trail: string[] = []): string[] | null {
  for (const n of nodes) {
    if (n.id === id) return [...trail, n.id];
    const deeper = n.children ? pathTo(n.children, id, [...trail, n.id]) : null;
    if (deeper) return deeper;
  }
  return null;
}

export function TreeList({ label, root, nodes, value, onChange, defaultExpanded, foot }: TreeListProps) {
  const [open, setOpen] = useState<Set<string>>(
    () =>
      new Set(
        defaultExpanded ??
          pathTo(nodes, value)
            ?.slice(0, -1)
            .concat(value ?? []) ??
          [],
      ),
  );
  const list = useRef<HTMLUListElement>(null);
  const toggle = (id: string, to?: boolean) =>
    setOpen((s) => {
      const n = new Set(s);
      if (to ?? !n.has(id)) n.add(id);
      else n.delete(id);
      return n;
    });
  const choose = (n: TreeNode | null) => {
    onChange(n?.id ?? null);
    if (n?.children?.length) toggle(n.id, true);
  };

  const rows = () => [...(list.current?.querySelectorAll<HTMLButtonElement>('.fl-tree__row') ?? [])];
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, n: TreeNode | null, parent: string | null) => {
    const all = rows();
    const at = all.indexOf(e.currentTarget);
    const go = (i: number) => all[Math.max(0, Math.min(all.length - 1, i))]?.focus();
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(all.length - 1);
    else if (e.key === 'ArrowRight' && n?.children?.length) {
      if (!open.has(n.id)) toggle(n.id, true);
      else go(at + 1);
    } else if (e.key === 'ArrowLeft') {
      if (n && open.has(n.id)) toggle(n.id, false);
      else if (parent) all.find((b) => b.dataset.id === parent)?.focus();
    } else return;
    e.preventDefault();
  };

  const branch = (items: TreeNode[], depth: number, parent: string | null): ReactNode => (
    <ul className="fl-tree__list">
      {items.map((n) => {
        const kids = depth < MAX_DEPTH && n.children?.length ? n.children : null;
        const isOpen = kids ? open.has(n.id) : undefined;
        return (
          <li key={n.id}>
            <button
              type="button"
              className="fl-tree__row"
              data-id={n.id}
              data-depth={depth}
              aria-current={value === n.id ? 'true' : undefined}
              aria-expanded={isOpen}
              onClick={() => (value === n.id && kids ? toggle(n.id) : choose(n))}
              onKeyDown={(e) => onKey(e, n, parent)}
            >
              <span className="fl-tree__label">{n.label}</span>
              {n.count != null ? <span className="fl-tree__count">{n.count}</span> : null}
            </button>
            {kids && isOpen ? branch(kids, depth + 1, n.id) : null}
          </li>
        );
      })}
    </ul>
  );

  return (
    <nav className="fl-tree" aria-label={label}>
      <ul className="fl-tree__list" ref={list}>
        {root ? (
          <li>
            <button
              type="button"
              className="fl-tree__row"
              data-depth="0"
              aria-current={value === null ? 'true' : undefined}
              onClick={() => choose(null)}
              onKeyDown={(e) => onKey(e, null, null)}
            >
              <span className="fl-tree__label">{root.label}</span>
              {root.count != null ? <span className="fl-tree__count">{root.count}</span> : null}
            </button>
          </li>
        ) : null}
        <li>{branch(nodes, 1, null)}</li>
      </ul>
      {foot ? <div className="fl-tree__foot">{foot}</div> : null}
    </nav>
  );
}
